import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { State } from '@state/core.state';
import { catchError, exhaustMap, map, mergeMap, of, take, firstValueFrom, from } from 'rxjs';
import { withLatestFrom, timeout, switchMap, tap } from 'rxjs/operators';

import {
	RestService,
	PROJECT_CARRIER_MAP_STATUS,
	TruckSearchTable,
	DATE_MDY,
	IS_NOT_NULL_OR_UNDEFINED,
	CarriersProjectsList,
	IS_NOT_EMPTY,
	Owner,
	TruckSearchResult,
	SEARCH_TRUCK_TABLE,
	SEARCH_CARRIER_ONLY_TABLE,
	DIFFERENCE_IN_DAYS,
	ProjectCarrierMapDTO,
	IS_NULL_OR_UNDEFINED,
	Carrier,
} from 'cargo-chief-commons';

import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { CsvExportService } from '@service/csv-export/csv-export.service';
import { ProjectHelperService } from '@service/projectHelper/project-helper.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import {
	CARGOCHIEF_KEYS,
	CAPACITY_SEARCH_FILTER,
	USER_SETTINGS,
} from '@domain/constants/app-variables';
import { IS_WORKFLOW_PROJECT_TYPE } from '@domain/constants/project-types';
import { FeedbackInformation } from '@notes/note/note.component';
import { TRUCK_TYPES } from '@domain/constants/truck-types';
import { ProductsService } from '@service/products/products.service';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { CapacitySearchFilterService } from '@service/capacitySearchFilter/capacity-search-filter.service';
import { ConfirmationDialogComponent } from '../../../common/confirmation-dialog/confirmation-dialog.component';
import { ProjectCarrierContactDialogComponent } from '../../project-carrier-contact-dialog/project-carrier-contact-dialog.component';
import { AddContactFormDialog } from '../../../contact-details/add-contact/add-contact.component';

import { ProjectDetailsStore } from '../store/project-details.store';
import { projectDetailsPageStateKey, ProjectDetailsState } from '../state';
import { RECOMMENDED_CARRIERS, PROJECT_CARRIER_MAP, PROJECT_DETAILS, PROJECT_GROUPS } from '../actions';
import { ProjectDetailsEffects } from '../effects/project-details.effects';

import { EngageCarriersDialogComponent } from '../../engage-carriers-dialog/engage-carriers-dialog.component';
import { LoginSetupService } from '@service/login-setup/login-setup.service';
import { ProjectGroupService } from '@service/project-group/project-group.service';
import { ProjectUtilService } from '@service/project-util/project-util.service';
import { AllocationService, AllocationDTO } from '@core/services/allocation/allocation.service';
import { AllocationDecisionDialogComponent, AllocationDecisionResult } from '../../allocation-decision-dialog/allocation-decision-dialog.component';
import { AllocationConfigDialogComponent, AllocationConfigResult } from '../../allocation-config-dialog/allocation-config-dialog.component';

// CN-8783: upper bound (ms) for the recommended-carriers capacity search so a hung
// backend call fails fast instead of holding the Recommended tab disabled indefinitely.
const CAPACITY_SEARCH_TIMEOUT_MS = 120000;

@Injectable({
	providedIn: 'root',
})
export class RecommendedCarriersEffects {
	constructor(
		private actions$: Actions<any>,
		private readonly store: Store<State>,
		public readonly projectDetailsStore: ProjectDetailsStore,
		private readonly restService: RestService,
		private readonly urlService: UrlService,
		public utilService: UtilService,
		private dialog: MatDialog,
		private csvExportService: CsvExportService,
		public projectHelperService: ProjectHelperService,
		public cargochief: Cargochief,
		public readonly projectDetailsEffects: ProjectDetailsEffects,
		public productsService: ProductsService,
		public accountSettings: AccountSettingsService,
		private loginSetupService: LoginSetupService,
		private capacitySearchFilterService: CapacitySearchFilterService,
		private projectGroupService: ProjectGroupService,
		private projectUtilService: ProjectUtilService,
		private allocationService: AllocationService
	) {}

	searchLanes$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RECOMMENDED_CARRIERS.SEARCH_LANES),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				if (this.productsService.ASSIGN_CARRIERS) {
					this.getAssigneeList(request[0].usersList);
				}

				if (request[0].hasLane) {
					return this.searchLanes(
						request[0].searchFormData,
						projectDetailsPageState.projectId,
						projectDetailsPageState,
						null
					);
				} else {
					return this.restService
						.GET(this.urlService.getCarriersByCustomerUrl(request[0].projectDTO.customerDTO.id))
						.pipe(
							map((result) => {
								this.searchLanes(
									request[0].searchFormData,
									projectDetailsPageState.projectId,
									projectDetailsPageState,
									result
								);
								return RECOMMENDED_CARRIERS.SEARCH_LANES_SUCCESS({});
							}),
							catchError((error) => {
								this.utilService.showSnackbar(
									'Unable to load recommended carriers list. Please try again later.',
									SNACKBAR.ERROR,
									SNACKBAR.ACTION
								);
								return RECOMMENDED_CARRIERS.SEARCH_LANES_FAILURE({});
							})
						);
				}
			})
		)
	);

	async searchLanes(searchFormData, projectId, projectDetailsPageState, customerOnlyCarriers) {
		try {
			let laneSearchResponse, carrierIds, truckSearchResult;
			if (customerOnlyCarriers) {
				laneSearchResponse = customerOnlyCarriers;
				truckSearchResult = laneSearchResponse['data'];

				truckSearchResult = customerOnlyCarriers.data.map((data) => {
					let truck = {} as TruckSearchResult;
					let carrier: Carrier = data;
					carrier.carrierHubId = carrier.id;

					truck.carrier = carrier;
					return truck;
				});

				carrierIds = laneSearchResponse['data'].map((search) => search.id);
			} else {
				const capacitySearchPayload = {
					...searchFormData,
					projectId,
				};
				let laneSearchResponse = await this.restService
					.POST(this.urlService.getCapacityUrl(), capacitySearchPayload)
					.pipe(
						timeout(CAPACITY_SEARCH_TIMEOUT_MS),
						catchError((error) => {
							console.error('laneSearchResponse failed: ' + error);
							this.utilService.showSnackbar(
								'Unable to run recommended carriers search.',
								SNACKBAR.ERROR,
								SNACKBAR.ACTION
							);
							return of({});
						})
					)
					.toPromise();

				laneSearchResponse.data.forEach((lane) => {
					lane['isLane'] = true;
				});
				this.store.dispatch(
					RECOMMENDED_CARRIERS.SET.TRUCK_LANE_SEARCH_ID({
						id:
							laneSearchResponse['data'].length > 0
								? laneSearchResponse['data'][0]['truckLaneSearchId']
								: null,
					})
				);
				truckSearchResult = laneSearchResponse['data'];
			}

			let allCapacitySearchResults: TruckSearchTable[] = [];
			if (truckSearchResult.length) {
				allCapacitySearchResults = SEARCH_TRUCK_TABLE(
					truckSearchResult,
					!this.utilService.isNullOrUndefined(searchFormData)
						? searchFormData.destination.state
						: ''
				);
				allCapacitySearchResults.forEach((truck: TruckSearchTable) => {
					if (
						(!truck.isLane || truck.isTruckAndLane) &&
						!this.utilService.isNullOrUndefined(searchFormData.pickupDateStart) &&
						!this.utilService.isNullOrUndefined(searchFormData.pickupDateEnd) &&
						DIFFERENCE_IN_DAYS(searchFormData.pickupDateStart, searchFormData.pickupDateEnd) > 1
					) {
						truck.showAvailableDate = true;
					}
				});
			}

			if (customerOnlyCarriers) {
				allCapacitySearchResults = SEARCH_CARRIER_ONLY_TABLE(truckSearchResult);
			}

			if (projectDetailsPageState.capacitySearchSort) {
				allCapacitySearchResults = this.capacitySearchFilterService.sortByColumn(
					projectDetailsPageState.capacitySearchSort,
					allCapacitySearchResults
				);
			}

			this.store.dispatch(
				RECOMMENDED_CARRIERS.SET.ALL_CAPACITY_SEARCH_RESULT({ list: allCapacitySearchResults })
			);
			if (customerOnlyCarriers) {
				this.projectDetailsEffects.setCapacitySearchMatchesList(allCapacitySearchResults);
			} else {
				this.projectDetailsEffects.setCapacitySearchMatchesList(truckSearchResult);
			}

			if (projectDetailsPageState.carrierHQ) {
				this.filterByHQ(projectDetailsPageState.carrierHQ, searchFormData);
			}

			this.projectDetailsEffects.setCapacitySearchDataForProjectCarrierMap();
			return RECOMMENDED_CARRIERS.SEARCH_LANES_SUCCESS({});
		} catch (exception) {
			console.error('recommended-carrers effect failed: ' + exception);
			return RECOMMENDED_CARRIERS.SEARCH_LANES_FAILURE({});
		}
	}

	getAssigneeList(usersList) {
		let userCarrierMapList: any = this.cargochief.getItem(CARGOCHIEF_KEYS.USER_CARRIER_MAPS);
		if (IS_NOT_EMPTY(userCarrierMapList)) {
			userCarrierMapList = JSON.parse(userCarrierMapList);
		} else {
			userCarrierMapList = [];
		}
		this.buildUserCarrierMap(userCarrierMapList, usersList);
	}
	buildUserCarrierMap(userCarrierMapList, usersList) {
		if (
			!this.utilService.isNullOrUndefined(userCarrierMapList) &&
			userCarrierMapList.length > 0 &&
			!this.utilService.isNullOrUndefined(usersList) &&
			usersList.length > 0
		) {
			let userCarrierMap: any = {};
			userCarrierMapList.forEach((cMap: any) => {
				const assignee: Owner = usersList.filter((user) => user.userId == cMap.assigneeId)[0];
				if (!this.utilService.isNullOrUndefined(assignee)) {
					userCarrierMap[cMap.carrierId] = assignee.name;
				}
			});
			this.store.dispatch(RECOMMENDED_CARRIERS.SET.USER_CARRIER.MAP({ userCarrierMap }));
			this.store.dispatch(RECOMMENDED_CARRIERS.SET.USER_CARRIER.LIST({ list: userCarrierMapList }));
		}
	}

	capacitySearchDownloadCSV$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RECOMMENDED_CARRIERS.CAPACITY_SEARCH.DOWNLOAD_CSV),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				const capacitySearchResults = request[0].capacitySearchResults,
					searchFormData = request[0].searchFormData;

				const fileHeaders = [
					'Carrier',
					'DOT',
					'MC',
					'Contact First Name',
					'Contact Last Name',
					'Contact Phone Number',
					'Email',
				];
				const truckSearchResult: TruckSearchTable[] = [...capacitySearchResults];
				let searchResults = [];
				let fileName = `Search Results.csv`;
				if (searchFormData) {
					fileName = `${searchFormData?.origin.city} ${searchFormData?.origin.state} - ${
						searchFormData?.destination.city
					} ${searchFormData?.destination.state} (${searchFormData?.trailerType
						.trim()
						.toUpperCase()
						.substring(0, 1)}) (${DATE_MDY(searchFormData.pickupDateStart)} - ${DATE_MDY(
						searchFormData.pickupDateEnd
					)})`;
				}

				if (truckSearchResult) {
					const url = this.urlService.getCarriersMailReceiverContacts();
					const request = {
						carrierIDs: truckSearchResult.map((searchResult) => searchResult.carrierId),
					};
					return this.restService.POST(url, request).pipe(
						timeout(120000),
						map((response: any) => {
							let result = [];
							truckSearchResult.forEach((searchResult) => {
								const contact = response.data.find(
									(contact) => contact.carrierId === searchResult.carrierId
								);
								let row = [
									`"${searchResult.businessName}"`,
									`"${searchResult.firmMc || ''}"`,
									`"${searchResult.firmDot || ''}"`,
									`"${contact?.firstName || ''}"`,
									`"${contact?.lastName || ''}"`,
									`"${contact?.phone || ''}"`,
									`"${contact?.emailAddress?.toLowerCase() || ''}"`,
								];
								result.push(row);
							});
							this.csvExportService.downloadCsvFile(fileName, fileHeaders, result);
							return RECOMMENDED_CARRIERS.CAPACITY_SEARCH.DOWNLOAD_COMPLETE({});
						}),
						catchError((error) => {
							this.utilService.showSnackbar(
								'Unable to fetch carrier contacts. Please try again.',
								SNACKBAR.ERROR,
								SNACKBAR.ACTION
							);
							return of(RECOMMENDED_CARRIERS.CAPACITY_SEARCH.DOWNLOAD_COMPLETE({}));
						})
					);
				}
				return of(RECOMMENDED_CARRIERS.CAPACITY_SEARCH.DOWNLOAD_COMPLETE({}));
			})
		)
	);

	engageWithCarriers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.START),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const [actionPayload, projectDetailsPageState]: [any, ProjectDetailsState] = request;
				const selectedCarrierIds = actionPayload.selectedCarrierIds,
					capacitySearchResults = projectDetailsPageState.capacitySearchResults,
					noteType = actionPayload.noteType,
					showRateForm = actionPayload.showRateForm,
					showNotes = actionPayload.showNotes;

				if (selectedCarrierIds.length === 1) {
					const carrierMatch: TruckSearchTable = capacitySearchResults.find(
						(searchResult) => searchResult.carrierId === selectedCarrierIds[0]
					);
					return of(
						RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.OPEN_DIALOG({
							carrierIds: selectedCarrierIds,
							noteType: noteType,
							projectId: projectDetailsPageState.projectId,
							carrierBusinessName: carrierMatch ? carrierMatch.businessName : null,
							feedbackInformation: carrierMatch
								? this.createFeedbackInformationFromOpenedMatch(carrierMatch)
								: null,
							showRateForm,
							showNotes,
						})
					);
				} else {
					return of(
						RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.CREATE({
							carrierIDs: selectedCarrierIds,
							desiredPrice: null,
							carrierRespondedAt: null,
							status: PROJECT_CARRIER_MAP_STATUS.IN_PROGRESS,
						})
					);
				}
			})
		)
	);

	engageWithCarrier$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.OPEN_DIALOG),
			exhaustMap((data) => {
				let dialogRef = this.dialog.open(EngageCarriersDialogComponent, {
					width: '550px',
					panelClass: 'cargo_chief_dialog_container',
					data,
				});
				return dialogRef.afterClosed();
			}),
			map((result: any) => {
				if (result) {
					if (IS_NOT_NULL_OR_UNDEFINED(result.hideModal)) {
						this.store.dispatch(
							RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.UPDATE_USER_SETTING({
								updatedSettingValue: result.hideModal,
							})
						);
					}
					this.store.dispatch(
						RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.CREATE({
							carrierIDs: result.carrierIDs,
							desiredPrice: result.desiredPrice,
							carrierRespondedAt: result.carrierRespondedAt,
							// CN-8661: forward the optional rate expiration date
							expirationDate: result.expirationDate,
							status: result.status,
							// CN-8312: Pass contactName and notes for note creation
							contactName: result.contactName,
							notes: result.notes,
							// CN-8312: forward the picked contact so the effect can
							// attach contactId to the new carrier-feedback row that
							// bumps Last Contact. Null when the user typed
							// free-form text instead of picking from the typeahead.
							selectedContact: result.selectedContact,
						})
					);
				}
				return RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.CLOSE({});
			})
		)
	);

	updateUserSetting$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.UPDATE_USER_SETTING),
			mergeMap((request) => {
				if (request) {
					const url = this.urlService.updateUserSettingUrl({
						settingName: USER_SETTINGS.HIDE_ENGAGE_CARRIER_DIALOG,
						value: request.updatedSettingValue,
					});
					return this.restService.PUT(url, {}).pipe(
						map((response: any) => {
							this.loginSetupService.updateLoginSetup();
							return RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.UPDATE_USER_SETTING_SUCCESS({});
						}),
						catchError((error) =>
							of(RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.UPDATE_USER_SETTING_FAILURE({}))
						)
					);
				}
			})
		)
	);

	addEngagedCarriersToProject$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.CREATE),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap(([action, projectDetailsPageState]: [any, ProjectDetailsState]) => {
				const payload = {
					carrierIDs: action.carrierIDs,
					desiredPrice: action.desiredPrice,
					carrierRespondedAt: action.carrierRespondedAt,
					// CN-8661: persist the carrier rate's expiration date when procuring with a rate
					expirationDate: action.expirationDate,
					status: action.status,
				};
				// CN-8312: Extract contactName and notes for note creation
				const contactName = action.contactName;
				const userNotes = action.notes;
				// CN-8312: Selected contact (or null) from the dialog typeahead.
				// Used to attach contactId to the carrier-feedback row that
				// bumps Last Contact on success.
				const selectedContact = action.selectedContact;
				return this.restService
					.POST(this.urlService.addProjectCarrierMap(projectDetailsPageState.projectId), payload)
					.pipe(
						switchMap((response: ProjectCarrierMapDTO[]) =>
							from(
								(async () => {
									// Update capacity search results
									const capacitySearchResults =
										projectDetailsPageState.capacitySearchResults.filter(
											(search: TruckSearchTable) => !payload.carrierIDs.includes(search.carrierId)
										);

									// Fetch additional project list data for carriers
									const carrierIds = response.map((pcr) => pcr.carrier.id);
									const carrierProjectListData =
										await this.projectUtilService.getCarrierProjectListData(carrierIds);
									response.forEach((pcr) => {
										pcr.projectsList = carrierProjectListData[pcr.carrier.id];
									});
									// Update engaged carriers list
									const engagedCarriersList = this.setPCMFieldsFromCapacitySearch(
										response,
										projectDetailsPageState.capacitySearchResults
									);

									const allProjectCarrierMapsList = [
										...engagedCarriersList,
										...projectDetailsPageState.allProjectCarrierMapsList,
									];

									// CN-8312: Save note with "Talked To:" prefix for each carrier
									if (contactName || userNotes) {
										let noteText = '';
										if (contactName && userNotes) {
											noteText = `Talked To: ${contactName}\n${userNotes}`;
										} else if (contactName) {
											noteText = `Talked To: ${contactName}`;
										} else {
											noteText = userNotes;
										}
										for (const carrierId of payload.carrierIDs) {
											const note = {
												carrierId: carrierId,
												notes: noteText,
												projectId: projectDetailsPageState.projectId,
											};
											try {
												await this.restService
													.POST(this.urlService.addCarrierNotes(), note)
													.toPromise();
											} catch (error) {
												console.error('Failed to save note for carrier:', carrierId, error);
											}
										}
									}

									// CN-8312: Write a carrier-feedback row for each carrier so the
									// project-details Last Contact field advances on engage. The
									// LastFeedbackDateService reads MAX(user_carrier_feedback.created)
									// per (account, carrier), so any new row bumps the date. We mark
									// reachedCarrier=true (the user just spoke to / contacted them) and
									// leave booked/rating/hasCapacity unset to avoid skewing analytics
									// that interpret those signals. contact.id carries through when the
									// user picked from the typeahead; free-form text leaves it null.
									//
									// Failure handling rationale: at this point /addProjectCarrierMap
									// and /carrierNotes have already succeeded — the carrier IS engaged
									// server-side. A feedback POST failure here only means Last Contact
									// won't advance; rolling back the engage would require deleting the
									// project_carrier_map + note (each of which can also fail). Instead,
									// we collect feedback failures and surface them to the user via a
									// warning snackbar after the engage success snackbar, so the user
									// is informed without losing the engage.
									const feedbackFailures: string[] = [];
									try {
										const account = this.cargochief.getAccount();
										const user = this.cargochief.getUser();
										const form = projectDetailsPageState.searchTruckForm;
										for (const carrierId of payload.carrierIDs) {
											const truckMatch = projectDetailsPageState.capacitySearchResults?.find(
												(s: TruckSearchTable) => s.carrierId === carrierId
											);
											const feedback: any = {
												accountId: account?.id,
												originCity: form?.origin?.city || null,
												originState: form?.origin?.state || null,
												destCity: form?.destination?.city || null,
												destState: form?.destination?.state || null,
												trailerTypes: form?.trailerType || null,
												contact: selectedContact ? { id: selectedContact.id } : {},
												user: { id: user?.id, firstName: user?.firstName },
												// CN-8312: leave booked unset (null) to match the "unset"
												// intent in the comment above. The carrier-feedback dialog
												// also leaves it unset for the same reason — `false` would
												// otherwise be a recorded answer to "did this load book?",
												// which the user never answered when engaging.
												booked: null,
												reachedCarrier: true,
												carrierId,
												truckId: truckMatch?.id || null,
												truckType: truckMatch?.isLane ? TRUCK_TYPES.LANE : TRUCK_TYPES.TRUCK,
												// CN-8312: search identifiers come from truckMatch.searchId
												// (the originating search), not truckMatch.id (the row id).
												// createFeedbackInformationFromOpenedMatch follows this same
												// pattern at lines 655-656 — feedback rows must reference
												// the searchId so they link back to the right search.
												truckSearchId:
													truckMatch && !truckMatch.isLane ? truckMatch.searchId : null,
												truckLaneSearchId:
													truckMatch && truckMatch.isLane ? truckMatch.searchId : null,
												rating: null,
												hasCapacity: null,
												userSuggestion: null,
											};
											try {
												await this.restService
													.POST(this.urlService.addCarrierFeedback(), feedback)
													.toPromise();
												// Locally bump the recentFeedbackDate so the card reflects
												// the change without waiting for a refresh.
												const now = new Date();
												engagedCarriersList.forEach((pcm) => {
													if (pcm.carrier?.id === carrierId) {
														pcm.recentFeedbackDate = now;
													}
												});
											} catch (feedbackError) {
												console.error(
													'Failed to record carrier feedback for engage:',
													carrierId,
													feedbackError
												);
												feedbackFailures.push(carrierId);
											}
										}
									} catch (outerError) {
										// Treat an outer failure (e.g. account/user lookup throwing) as
										// every carrier failing — the user should still see a warning.
										console.error('Engage feedback block failed:', outerError);
										payload.carrierIDs.forEach((id: string) => feedbackFailures.push(id));
									}
									// Surface any feedback failures via a warning snackbar so the user
									// knows Last Contact didn't advance for the affected carriers.
									if (feedbackFailures.length > 0) {
										const msg =
											feedbackFailures.length === payload.carrierIDs.length
												? 'Carrier engaged, but Last Contact could not be updated.'
												: `Carrier engaged, but Last Contact could not be updated for ${feedbackFailures.length} of ${payload.carrierIDs.length} carriers.`;
										this.utilService.showSnackbar(msg, SNACKBAR.ERROR, SNACKBAR.ACTION);
									}

									// CN-8312: build the engaged sub-list from the freshly mutated
									// allProjectCarrierMapsList so dispatches reflect the new
									// recentFeedbackDate. The tap below historically also called
									// setEngagedCarriers with the *old* state snapshot, which would
									// clobber our updates — that path is now removed.
									const newEngagedList = allProjectCarrierMapsList.filter(
										(pcm) => pcm.status === PROJECT_CARRIER_MAP_STATUS.IN_PROGRESS
									);

									// Dispatch actions as part of the effect stream
									return [
										RECOMMENDED_CARRIERS.SET.CAPACITY_SEARCH_RESULT({
											list: capacitySearchResults,
										}),
										RECOMMENDED_CARRIERS.SET.NUMBER_OF_LANES({
											count: capacitySearchResults.length,
										}),
										PROJECT_CARRIER_MAP.SET.ALL_PROJECT_CARRIER_MAPS_LIST({
											list: allProjectCarrierMapsList,
										}),
										PROJECT_CARRIER_MAP.SET.ENGAGED_CARRIERS_LIST({
											list: newEngagedList,
										}),
										PROJECT_CARRIER_MAP.SET.ENGAGED_CARRIERS_COUNT({
											total: newEngagedList.length,
										}),
										RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.SUCCESS({}),
									];
								})()
							).pipe(
								mergeMap((actions) => actions), // Flatten array of actions into the stream
								tap(() => {
									this.utilService.showSnackbar(
										`Carrier(s) added to engaged.`,
										SNACKBAR.SUCCESS,
										SNACKBAR.ACTION
									);
									// CN-8312: previously called setEngagedCarriers here with the
									// pre-engage state snapshot, which dispatched the engaged list
									// without the new pcm + without the recentFeedbackDate we just
									// set. That dispatch is now done atomically in the action stream
									// above with the freshly-mutated list, so the tap only handles
									// the side effects that aren't part of the engaged-list flow.
									this.projectUtilService.updateAccountCarrierLanes(
										response,
										projectDetailsPageState
									);
									this.projectDetailsEffects.setCapacitySearchDataForProjectCarrierMap();
								})
							)
						),
						catchError((error) => {
							this.utilService.showSnackbar(
								'Unable to add carriers to project now.',
								SNACKBAR.ERROR,
								SNACKBAR.ACTION
							);
							return of(
								RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.FAILURE({
									payload: { message: error.error },
								})
							);
						})
					);
			})
		)
	);

	createFeedbackInformationFromOpenedMatch(table: TruckSearchTable): FeedbackInformation {
		return {
			originCity: table.originCity,
			originState: table.originState,
			destCity: table.destCity,
			destState: table.destState,
			trailerTypes: table.trailerTypes,
			truckId: table.id,
			truckSearchId: !table.isLane ? table.searchId : null,
			truckLaneSearchId: table.isLane ? table.searchId : null,
			truckType: table.isLane ? TRUCK_TYPES.LANE : TRUCK_TYPES.TRUCK,
		};
	}

	recommendedToProcure$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RECOMMENDED_CARRIERS.PROCURE_CARRIERS.CREATE),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				const selectedCarrierIds = request[0].selectedCarrierIds;
				const useEngageDialog = !!request[0].useEngageDialog;
				if (selectedCarrierIds.length === 1) {
					// CN-8312 V2: route single-carrier procure through the same
					// EngageCarriersDialog used by recommended→engaged. The dialog
					// captures who-talked-to + notes + rate; on success we write
					// a note and a feedback row so Last Contact advances.
					if (useEngageDialog) {
						return this.recommendCarrierProcureWithEngageDialog(
							selectedCarrierIds,
							projectDetailsPageState
						);
					}
					return this.recommendCarrierProcureWithRate(selectedCarrierIds, projectDetailsPageState);
				}
				return this.moveCarriersFromRecommendedToProcured(
					selectedCarrierIds,
					projectDetailsPageState
				);
			})
		)
	);

	async moveCarriersFromRecommendedToProcured(
		selectedCarrierIds: string[],
		projectDetailsPageState: ProjectDetailsState
	) {
		const { projectGroupPayload, noGroupSelected } =
			await this.selectProjectGroup(projectDetailsPageState);
		if (noGroupSelected) {
			this.utilService.showSnackbar(
				'Cannot add carrier to project without selecting a group',
				SNACKBAR.ERROR,
				SNACKBAR.ACTION
			);
			return;
		}
		const payload = {
			carrierIDs: selectedCarrierIds,
			desiredPrice: null,
			carrierRespondedAt: null,
			status: PROJECT_CARRIER_MAP_STATUS.PROCURED,
			...projectGroupPayload,
		};
		let procuredCarriersResponse = await this.restService
			.POST(this.urlService.addProjectCarrierMap(projectDetailsPageState.projectId), payload)
			.toPromise();
		return this.afterMovingCarriersFromRecommendedToProcure(
			selectedCarrierIds,
			procuredCarriersResponse,
			projectDetailsPageState
		);
	}

	async recommendCarrierProcureWithRate(
		selectedCarrierIds,
		projectDetailsPageState: ProjectDetailsState
	) {
		const { projectGroupPayload, noGroupSelected } =
			await this.selectProjectGroup(projectDetailsPageState);
		if (noGroupSelected) {
			this.utilService.showSnackbar(
				'Cannot add carrier to project without selecting a group',
				SNACKBAR.ERROR,
				SNACKBAR.ACTION
			);
			return;
		}
		let desiredPrice = null,
			carrierRespondedAt = null,
			expirationDate = null;
		let result = await this.projectHelperService.openCarrierRateDialog({
			hasRate: false,
			title: 'Do you have a rate for this?',
		});
		desiredPrice = result && result.desiredPrice;
		carrierRespondedAt = result && result.carrierRespondedAt;
		expirationDate = result && result.expirationDate;
		let projectCarrierMapPrimaryContact = null,
			projectCarrierContactSelection = null;
		const maxBuy = projectDetailsPageState.projectDTO.maxBuy,
			carrier = projectDetailsPageState.capacitySearchResults.find(
				(search: TruckSearchTable) => selectedCarrierIds.indexOf(search.carrierId) !== -1
			);
		const confirmationResponse = await this.projectDetailsEffects.getMaxBuyExceedConfirmation(
			desiredPrice,
			maxBuy,
			carrier?.businessName || ''
		);
		if (!confirmationResponse) {
			desiredPrice = null;
			carrierRespondedAt = null;
			expirationDate = null;
		}

		// ask for contact
		const setPrimaryContactResponse = await this.dialog
			.open(ConfirmationDialogComponent, {
				width: '500px',
				data: {
					title: '',
					body: `Do you want to set the Project contact for the carrier?`,
					cancelButtonText: 'No',
					ConfirmButtonText: 'Yes',
				},
				disableClose: true,
			})
			.afterClosed()
			.toPromise();
		if (setPrimaryContactResponse) {
			projectCarrierContactSelection = await this.dialog
				.open(ProjectCarrierContactDialogComponent, {
					width: '500px',
					data: {
						carrier,
					},
					disableClose: true,
				})
				.afterClosed()
				.toPromise();
			if (projectCarrierContactSelection) {
				if (projectCarrierContactSelection?.addContact) {
					const addNewContactResponse = await this.dialog
						.open(AddContactFormDialog, {
							width: '500px',
							data: {
								carrierid: carrier.carrierId,
								isEdit: false,
								contact: null,
							},
						})
						.afterClosed()
						.toPromise();
					if (addNewContactResponse) {
						projectCarrierMapPrimaryContact = addNewContactResponse;
					}
				} else {
					projectCarrierMapPrimaryContact = projectCarrierContactSelection;
				}
			}
		}
		const payload = {
			carrierIDs: selectedCarrierIds,
			desiredPrice,
			carrierRespondedAt,
			expirationDate,
			status: PROJECT_CARRIER_MAP_STATUS.PROCURED,
			...projectGroupPayload,
		};

		let procuredCarriersResponse = await this.restService
			.POST(this.urlService.addProjectCarrierMap(projectDetailsPageState.projectId), payload)
			.toPromise();
		if (projectCarrierContactSelection?.addContact && projectCarrierMapPrimaryContact) {
			const newContactId = projectCarrierMapPrimaryContact.contactId;
			projectCarrierMapPrimaryContact = null;
			for (const pcm of procuredCarriersResponse) {
				const contact = pcm.carrier.contacts.find((contact) => contact.id === newContactId);
				if (contact) {
					projectCarrierMapPrimaryContact = contact;
					break;
				}
			}
		}
		if (projectCarrierMapPrimaryContact) {
			const pcmContactResponse = await this.mapContactToProject(
				procuredCarriersResponse[0].id,
				projectCarrierMapPrimaryContact.contactId
			);
			if (pcmContactResponse) {
				procuredCarriersResponse.forEach((pcm) => {
					if (pcm.id == pcmContactResponse?.id) {
						pcm.contactDTOs = pcmContactResponse.contactDTOs;
						pcm.primaryCarrierContactMapId = pcmContactResponse.primaryCarrierContactMapId;
					}
				});
			}
		}

		// For workflow projects, show the allocation configuration dialogs
		if (this.isWorkflowProject(projectDetailsPageState) && carrier) {
			await this.handleAllocationFlow(
				carrier.carrierId,
				carrier.businessName,
				projectDetailsPageState.projectId
			);
		}

		return this.afterMovingCarriersFromRecommendedToProcure(
			selectedCarrierIds,
			procuredCarriersResponse,
			projectDetailsPageState
		);
	}

	async mapContactToProject(projectCarrierMapId: string, contactId: string) {
		let contactUpdateResponse = null;
		try {
			const updateRequest = {
				accountId: this.cargochief.ACCOUNT_ID,
				projectCarrierMapId,
				contactId,
			};
			contactUpdateResponse = await this.restService
				.POST(this.urlService.updateProjectCarrierMapContact(projectCarrierMapId), updateRequest)
				.toPromise();
		} catch (error) {
			this.utilService.showSnackbar(
				'Unable to set project contact.',
				SNACKBAR.ERROR,
				SNACKBAR.ACTION
			);
		}
		return contactUpdateResponse;
	}

	/**
	 * CN-8312 V2: Recommended → Procured flow that opens the same
	 * EngageCarriersDialog as recommended → engaged. The dialog captures
	 * who-talked-to (typeahead over the carrier's contacts) + notes + rate +
	 * rate-received date. After the procure POST succeeds, we save the note
	 * with the standard "Talked To: X" prefix and write a feedback row so
	 * Last Contact advances.
	 */
	async recommendCarrierProcureWithEngageDialog(
		selectedCarrierIds: string[],
		projectDetailsPageState: ProjectDetailsState
	) {
		const carrierMatch = projectDetailsPageState.capacitySearchResults.find(
			(search: TruckSearchTable) => selectedCarrierIds.indexOf(search.carrierId) !== -1
		);
		const isWorkflow = this.isWorkflowProject(projectDetailsPageState);
		const dialogResult: any = await this.dialog
			.open(EngageCarriersDialogComponent, {
				width: '550px',
				panelClass: 'cargo_chief_dialog_container',
				data: {
					carrierIds: selectedCarrierIds,
					noteType: null,
					projectId: projectDetailsPageState.projectId,
					carrierBusinessName: carrierMatch?.businessName || null,
					feedbackInformation: carrierMatch
						? this.createFeedbackInformationFromOpenedMatch(carrierMatch)
						: null,
					showRateForm: true,
					showNotes: true,
					submitButtonText: isWorkflow ? 'Add to Waterfall' : 'Procure',
					dialogHeader: isWorkflow ? 'Add to Waterfall' : 'Log a call',
					// CN-8312: procure-side users may just want to record the
					// move without logging a call. Make every dialog input
					// optional — the carrier still procures, and Last Contact
					// stays null when nothing is entered (handled below in
					// persistEngageDialogSideEffects).
					contactNameOptional: true,
				},
			})
			.afterClosed()
			.toPromise();
		if (!dialogResult) {
			// User cancelled — leave the carrier in Recommended. Don't dispatch
			// PROCURE_CARRIERS.SUCCESS: project-details.component listens to it
			// to clear selection, refresh notes, etc. — that's wrong on cancel.
			// Emit a no-op action with no reducers/effects listening.
			return { type: '[Project details] Procure dialog cancelled' } as any;
		}

		// Max-buy confirmation if a rate was entered above maxBuy.
		let desiredPrice = dialogResult.desiredPrice ?? null;
		let carrierRespondedAt = dialogResult.carrierRespondedAt ?? null;
		const maxBuy = projectDetailsPageState.projectDTO?.maxBuy;
		const confirmationResponse = await this.projectDetailsEffects.getMaxBuyExceedConfirmation(
			desiredPrice,
			maxBuy,
			carrierMatch?.businessName || ''
		);
		if (!confirmationResponse) {
			desiredPrice = null;
			carrierRespondedAt = null;
		}

		const payload: any = {
			carrierIDs: selectedCarrierIds,
			desiredPrice,
			carrierRespondedAt,
			status: PROJECT_CARRIER_MAP_STATUS.PROCURED,
		};

		const procuredCarriersResponse: ProjectCarrierMapDTO[] = await this.restService
			.POST(this.urlService.addProjectCarrierMap(projectDetailsPageState.projectId), payload)
			.toPromise();

		// Persist the captured note + feedback in parallel with the rest of
		// the procure flow. Both reuse the same logic the engage flow uses;
		// they're best-effort writes — failures show a warning snackbar but
		// don't block the procure success.
		await this.persistEngageDialogSideEffects(
			selectedCarrierIds,
			projectDetailsPageState,
			carrierMatch || null,
			dialogResult,
			procuredCarriersResponse
		);

		if (isWorkflow && carrierMatch) {
			await this.handleAllocationFlow(
				carrierMatch.carrierId,
				carrierMatch.businessName,
				projectDetailsPageState.projectId
			);
		}

		return this.afterMovingCarriersFromRecommendedToProcure(
			selectedCarrierIds,
			procuredCarriersResponse,
			projectDetailsPageState
		);
	}

	/**
	 * CN-8312 V2: shared logic for saving the note + feedback row that the
	 * EngageCarriersDialog flows produce. Used by both the
	 * recommended→engaged flow (already inlined elsewhere — kept inline
	 * there to minimise diff in that path) and the new
	 * recommended→procured / engaged→procured flows.
	 */
	async persistEngageDialogSideEffects(
		carrierIDs: string[],
		projectDetailsPageState: ProjectDetailsState,
		truckMatch: TruckSearchTable | null,
		dialogResult: any,
		pcmResponse: ProjectCarrierMapDTO[]
	) {
		const contactName = dialogResult?.contactName;
		const userNotes = dialogResult?.notes;
		const selectedContact = dialogResult?.selectedContact;

		// CN-8312 V2: a write failure on either the carrierNote OR the
		// carrierFeedback POST should be surfaced — both contribute to what the
		// user expected to land in the carrier record (note in the panel,
		// Last Contact bump). Combine them into one warning snackbar at the end.
		const noteFailures: string[] = [];
		const feedbackFailures: string[] = [];

		// Save note with "Talked To:" prefix when either contactName or notes
		// were entered. Same shape as the engage flow's note write.
		if (contactName || userNotes) {
			let noteText = '';
			if (contactName && userNotes) {
				noteText = `Talked To: ${contactName}\n${userNotes}`;
			} else if (contactName) {
				noteText = `Talked To: ${contactName}`;
			} else {
				noteText = userNotes;
			}
			for (const carrierId of carrierIDs) {
				const note = {
					carrierId,
					notes: noteText,
					projectId: projectDetailsPageState.projectId,
				};
				try {
					await this.restService
						.POST(this.urlService.addCarrierNotes(), note)
						.toPromise();
				} catch (err) {
					console.error('Failed to save note for carrier:', carrierId, err);
					noteFailures.push(carrierId);
				}
			}
		}

		// CN-8312: skip the carrier-feedback POST only when the user submitted
		// the dialog with no real input — Last Contact stays null in that case.
		// Any of contactName, selectedContact, notes, or rate (desiredPrice)
		// counts as "the user did something on this carrier" and bumps
		// Last Contact. carrierRespondedAt isn't checked because it defaults
		// to today's date even if the user doesn't touch the field.
		// Engage flow has Validators.required on contactName so this gate only
		// short-circuits in the procure-flow path (contactNameOptional=true).
		const hasAnyInput =
			(typeof contactName === 'string' && contactName.trim().length > 0) ||
			!!selectedContact ||
			(typeof userNotes === 'string' && userNotes.trim().length > 0) ||
			!!dialogResult?.desiredPrice;
		if (!hasAnyInput) {
			return;
		}

		// Write a carrier-feedback row per carrier so Last Contact advances.
		// Mirrors the engage-flow logic (recommended→engaged) — booked: null
		// (unset), reachedCarrier: true, contact.id when the user picked a
		// contact from the typeahead.
		try {
			const account = this.cargochief.getAccount();
			const user = this.cargochief.getUser();
			const form = projectDetailsPageState.searchTruckForm;
			for (const carrierId of carrierIDs) {
				// CN-8312 V2: prefer the explicit truckMatch passed in (engage flow),
				// then capacitySearchResults (current Recommended view), then fall
				// back to allCapacitySearchResults (the unfiltered superset). The
				// last fallback is required for engaged→procured because capacityResults
				// no longer includes the carrier — but the carrier-hub feedback
				// validator (AddFeedbackRequestValidator) requires non-null truckId
				// and truckType.
				const tm =
					truckMatch?.carrierId === carrierId
						? truckMatch
						: projectDetailsPageState.capacitySearchResults?.find(
								(s: TruckSearchTable) => s.carrierId === carrierId
							) ||
							projectDetailsPageState.allCapacitySearchResults?.find(
								(s: TruckSearchTable) => s.carrierId === carrierId
							) ||
							null;
				// CN-8312 V2: the carrier-hub feedback validator (AddFeedbackRequestValidator)
				// rejects requests with null truckId/truckType. If we couldn't resolve the
				// carrier from any search result list (e.g. fully cold-loaded engaged
				// carrier from a prior session), don't POST — mark it failed locally so
				// the user sees the same warning snackbar as a network failure would
				// produce, instead of guaranteeing a 422 round trip.
				if (!tm?.id) {
					feedbackFailures.push(carrierId);
					continue;
				}
				const feedback: any = {
					accountId: account?.id,
					originCity: form?.origin?.city || null,
					originState: form?.origin?.state || null,
					destCity: form?.destination?.city || null,
					destState: form?.destination?.state || null,
					trailerTypes: form?.trailerType || null,
					contact: selectedContact ? { id: selectedContact.id } : {},
					user: { id: user?.id, firstName: user?.firstName },
					booked: null,
					reachedCarrier: true,
					carrierId,
					truckId: tm.id,
					truckType: tm.isLane ? TRUCK_TYPES.LANE : TRUCK_TYPES.TRUCK,
					truckSearchId: !tm.isLane ? tm.searchId : null,
					truckLaneSearchId: tm.isLane ? tm.searchId : null,
					rating: null,
					hasCapacity: null,
					userSuggestion: null,
				};
				try {
					await this.restService
						.POST(this.urlService.addCarrierFeedback(), feedback)
						.toPromise();
					const now = new Date();
					if (Array.isArray(pcmResponse)) {
						pcmResponse.forEach((pcm) => {
							if (pcm.carrier?.id === carrierId) {
								pcm.recentFeedbackDate = now;
							}
						});
					}
				} catch (feedbackError) {
					console.error(
						'Failed to record carrier feedback on procure:',
						carrierId,
						feedbackError
					);
					feedbackFailures.push(carrierId);
				}
			}
		} catch (outerError) {
			console.error('Procure feedback block failed:', outerError);
			carrierIDs.forEach((id) => feedbackFailures.push(id));
		}
		// Emit a single warning snackbar covering both note + feedback write
		// failures. Each piece is reported with its own line so the user knows
		// exactly what didn't land.
		if (noteFailures.length > 0 || feedbackFailures.length > 0) {
			const parts: string[] = [];
			if (noteFailures.length > 0) {
				parts.push(
					noteFailures.length === carrierIDs.length
						? 'note could not be saved'
						: `note could not be saved for ${noteFailures.length} of ${carrierIDs.length} carriers`
				);
			}
			if (feedbackFailures.length > 0) {
				parts.push(
					feedbackFailures.length === carrierIDs.length
						? 'Last Contact could not be updated'
						: `Last Contact could not be updated for ${feedbackFailures.length} of ${carrierIDs.length} carriers`
				);
			}
			this.utilService.showSnackbar(
				`Carrier procured, but ${parts.join(' and ')}.`,
				SNACKBAR.ERROR,
				SNACKBAR.ACTION
			);
		}
	}

	async afterMovingCarriersFromRecommendedToProcure(
		selectedCarrierIds,
		procuredCarriersResponse: ProjectCarrierMapDTO[],
		projectDetailsPageState: ProjectDetailsState
	) {
		const carrierIds = procuredCarriersResponse.map((pcr) => pcr.carrier.id);
		const carrierProjectListData =
			await this.projectUtilService.getCarrierProjectListData(carrierIds);
		procuredCarriersResponse.forEach((pcr) => {
			pcr.projectsList = carrierProjectListData[pcr.carrier.id];
		});
		const isWorkflowProject = IS_WORKFLOW_PROJECT_TYPE(projectDetailsPageState.projectDTO?.type);
		const successMessage = isWorkflowProject ? `Carrier(s) added to waterfall.` : `Carrier(s) moved to procured.`;
		this.utilService.showSnackbar(successMessage, SNACKBAR.SUCCESS, SNACKBAR.ACTION);
		let procuredCarriersList = this.setPCMFieldsFromCapacitySearch(
			procuredCarriersResponse,
			projectDetailsPageState.capacitySearchResults
		);
		const allProjectCarrierMapsList = procuredCarriersList.concat(
			projectDetailsPageState.allProjectCarrierMapsList
		);
		this.projectDetailsEffects.setProcuredCarriers(allProjectCarrierMapsList);
		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.ALL_PROJECT_CARRIER_MAPS_LIST({
				list: allProjectCarrierMapsList,
			})
		);
		const capacitySearchResults = [].concat(
			projectDetailsPageState.capacitySearchResults.filter(
				(search: TruckSearchTable) => selectedCarrierIds.indexOf(search.carrierId) === -1
			)
		);
		this.store.dispatch(
			RECOMMENDED_CARRIERS.SET.CAPACITY_SEARCH_RESULT({
				list: capacitySearchResults,
			})
		);
		this.projectUtilService.updateAccountCarrierLanes(
			procuredCarriersResponse,
			projectDetailsPageState
		);
		this.projectDetailsEffects.setCapacitySearchDataForProjectCarrierMap();
		return RECOMMENDED_CARRIERS.PROCURE_CARRIERS.SUCCESS({});
	}

	setPCMFieldsFromCapacitySearch(
		projectCarrierMapList: ProjectCarrierMapDTO[],
		capacitySearchResults
	) {
		projectCarrierMapList.forEach((pcm) => {
			const search = capacitySearchResults.find((search) => search.carrierId === pcm.carrier.id);
			if (search) {
				pcm.laneCount = search.lanesCount;
				pcm.notesCount = search.notesCount;
				if (IS_NULL_OR_UNDEFINED(pcm.carrier.customerCarrier)) {
					pcm.carrier.customerCarrier =
						pcm.carrier.accountIdSources?.indexOf(this.cargochief.ACCOUNT_ID) !== -1;
				}
				if (
					IS_NULL_OR_UNDEFINED(pcm.recentFeedbackDate) &&
					IS_NOT_NULL_OR_UNDEFINED(search.recentFeedbackDate)
				) {
					pcm.recentFeedbackDate = search.recentFeedbackDate;
				}
			}
		});
		return projectCarrierMapList;
	}

	async filterByHQ(filterSelected: string, searchTruckForm: any) {
		this.store
			.select((state) => state[projectDetailsPageStateKey])
			.pipe(take(1))
			.subscribe(async (state) => {
				const DHO = this.cargochief.getItem(CARGOCHIEF_KEYS.ORIGIN_DEADHEAD_SLIDER),
					DHD = this.cargochief.getItem(CARGOCHIEF_KEYS.DESTINATION_DEADHEAD_SLIDER);
				let latitude, longitude, maxRadius;
				if (filterSelected == CAPACITY_SEARCH_FILTER.CARRIER_HQ[0]) {
					latitude = searchTruckForm.origin.latitude;
					longitude = searchTruckForm.origin.longitude;
					if (!this.utilService.isNullOrUndefined(DHO)) {
						maxRadius = JSON.parse(DHO);
					} else {
						maxRadius = this.accountSettings.SEARCH_TRUCK_ORIGIN_DEADHEAD_LIMIT;
					}
				} else {
					latitude = searchTruckForm.destination.latitude;
					longitude = searchTruckForm.destination.longitude;
					if (!this.utilService.isNullOrUndefined(DHD)) {
						maxRadius = JSON.parse(DHD);
					} else {
						maxRadius = this.accountSettings.SEARCH_TRUCK_DESTINATION_DEADHEAD_LIMIT;
					}
				}
				const capacitySearchResultsCarrierIds: string[] = state.allCapacitySearchResults.map(
					(search) => search.carrierId
				);
				if (!capacitySearchResultsCarrierIds.length) {
					return;
				}
				const payload = {
					carrierIds: capacitySearchResultsCarrierIds.join(','),
					latitude: latitude,
					longitude: longitude,
					maxRadius: maxRadius,
				};

				const url = this.urlService.searchCarrierHQ(0, capacitySearchResultsCarrierIds.length);
				const carrierHQResponse = await this.restService.POST(url, payload).toPromise();
				let capacitySearchResults = this.projectDetailsEffects.getAllUniqueCapacitySearchResults(
					state.allCapacitySearchResults,
					state.allProjectCarrierMapsList,
					state.powerUnits
				);
				const matchedCarrierHqIds = carrierHQResponse.data.map((carrier) => carrier.id);
				capacitySearchResults = capacitySearchResults.filter(
					(search) => matchedCarrierHqIds.indexOf(search.carrierId) !== -1
				);
				this.store.dispatch(
					RECOMMENDED_CARRIERS.SET.MATCHED_CARRIER_HQ_IDS({ carrierIds: matchedCarrierHqIds })
				);
				this.projectDetailsEffects.setCapacitySearchResults(capacitySearchResults);
			});
	}

	removeHQFilter() {
		this.store
			.select((state) => state[projectDetailsPageStateKey])
			.pipe(take(1))
			.subscribe(async (state) => {
				let capacitySearchResults = this.projectDetailsEffects.getAllUniqueCapacitySearchResults(
					state.allCapacitySearchResults,
					state.allProjectCarrierMapsList,
					state.powerUnits
				);
				this.store.dispatch(RECOMMENDED_CARRIERS.SET.MATCHED_CARRIER_HQ_IDS({ carrierIds: [] }));
				this.projectDetailsEffects.setCapacitySearchResults(capacitySearchResults);
			});
	}

	async selectProjectGroup(projectDetailsPageState: ProjectDetailsState): Promise<{
		projectGroupPayload: { manageProjectGroup?: boolean; projectGroupId?: string };
		noGroupSelected: boolean;
	}> {
		const project = projectDetailsPageState.projectDTO;
		let projectGroupPayload = {};
		let noGroupSelected = false;
		if (IS_WORKFLOW_PROJECT_TYPE(project.type)) {
			let { projectGroupId, projectHasNoGroups } =
				await this.projectGroupService.selectProjectGroup(project.id);
			if (projectHasNoGroups) {
				projectGroupPayload = {
					manageProjectGroup: true,
				};
			} else if (IS_NOT_NULL_OR_UNDEFINED(projectGroupId)) {
				projectGroupPayload = {
					projectGroupId,
				};
			} else {
				noGroupSelected = true;
			}
		}
		return new Promise((resolve) => resolve({ projectGroupPayload, noGroupSelected }));
	}

	/**
	 * Shows the allocation decision dialog for workflow projects
	 * Returns true if the user wants to configure allocation, false to skip
	 */
	async showAllocationDecisionDialog(
		carrierBusinessName: string,
		existingAllocation: AllocationDTO | null
	): Promise<AllocationDecisionResult | null> {
		const dialogRef = this.dialog.open(AllocationDecisionDialogComponent, {
			width: '500px',
			panelClass: 'cargo_chief_dialog_container',
			data: {
				carrierBusinessName,
				existingAllocation: existingAllocation
					? {
							allocationType: existingAllocation.allocationType,
							allocationValue: existingAllocation.allocationValue,
							allocationPeriod: existingAllocation.allocationPeriod,
						}
					: null,
			},
			disableClose: false,
		});

		return dialogRef.afterClosed().toPromise();
	}

	/**
	 * Fetches the default allocation window from the Booking Assistant configuration
	 */
	async getDefaultAllocationWindowMinutes(): Promise<number> {
		try {
			const response = await this.restService.GET(this.urlService.getBookingAutomationConfigurationData()).toPromise();

			if (response?.data) {
				const configData = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
				return configData.defaultAllocationWindowMinutes || 15;
			}

			return 15;
		} catch (error) {
			// If we can't fetch the config, use the default value
			return 15;
		}
	}

	/**
	 * Shows the allocation configuration dialog
	 */
	async showAllocationConfigDialog(
		carrierBusinessName: string,
		carrierId: string,
		projectId: string,
		existingAllocation: AllocationDTO | null,
		defaultAllocationWindowMinutes?: number
	): Promise<AllocationConfigResult | null> {
		const dialogRef = this.dialog.open(AllocationConfigDialogComponent, {
			width: '550px',
			panelClass: 'cargo_chief_dialog_container',
			data: {
				carrierBusinessName,
				carrierId,
				projectId,
				defaultAllocationWindowMinutes,
				existingAllocation: existingAllocation
					? {
							id: existingAllocation.id,
							allocationType: existingAllocation.allocationType,
							allocationValue: existingAllocation.allocationValue,
							allocationPeriod: existingAllocation.allocationPeriod,
							allocationWindowMinutes: existingAllocation.allocationWindowMinutes,
							isActive: existingAllocation.isActive,
							effectiveStartDate: existingAllocation.effectiveStartDate,
							effectiveEndDate: existingAllocation.effectiveEndDate,
						}
					: null,
			},
			disableClose: false,
		});

		return dialogRef.afterClosed().toPromise();
	}

	/**
	 * Handles the allocation flow for workflow projects when adding a carrier to procured
	 * @param skipConfirmation - If true, skip the decision dialog and go straight to config
	 *                          (used when user explicitly clicks Add/Edit Allocation from Actions menu)
	 * @param addToWaterfall - If true, also add the carrier to the Waterfall after creating allocation
	 *                        (used when allocation is created from Recommended tab)
	 * @param moveToLowestPriorityGroup - If true, move existing carrier to lowest priority group after allocation
	 *                                    (used when allocation is created for carrier already in waterfall)
	 * @param projectCarrierMapId - The ID of the project carrier map (required if moveToLowestPriorityGroup is true)
	 */
	async handleAllocationFlow(
		carrierId: string,
		carrierBusinessName: string,
		projectId: string,
		skipConfirmation: boolean = false,
		addToWaterfall: boolean = false,
		moveToLowestPriorityGroup: boolean = false,
		projectCarrierMapId?: string
	): Promise<void> {
		// Validate dependent parameters
		if (moveToLowestPriorityGroup && !projectCarrierMapId) {
			console.error('projectCarrierMapId is required when moveToLowestPriorityGroup is true');
			return;
		}

		try {
			// Check if there's an existing allocation for this carrier-project combination
			const existingAllocation = await this.allocationService
				.getAllocation(projectId, carrierId)
				.toPromise();

			// Show the decision dialog (unless skipped)
			if (!skipConfirmation) {
				const decisionResult = await this.showAllocationDecisionDialog(
					carrierBusinessName,
					existingAllocation
				);

				if (!decisionResult || decisionResult.decision === 'skip') {
					return; // User chose to skip allocation
				}
			}

			// Fetch the BA config default for new allocations
			const defaultAllocationWindowMinutes = !existingAllocation
				? await this.getDefaultAllocationWindowMinutes()
				: undefined;

			// User wants to configure allocation
			const configResult = await this.showAllocationConfigDialog(
				carrierBusinessName,
				carrierId,
				projectId,
				existingAllocation,
				defaultAllocationWindowMinutes
			);

			if (!configResult) {
				return; // User cancelled the config dialog
			}

			// Save the allocation
			const allocationPayload: AllocationDTO = {
				projectId,
				carrierId,
				allocationType: configResult.allocationType,
				allocationValue: configResult.allocationValue,
				allocationPeriod: configResult.allocationPeriod,
				allocationWindowMinutes: configResult.allocationWindowMinutes,
				isActive: configResult.isActive,
				// CN-8662: optional effective window
				effectiveStartDate: configResult.effectiveStartDate,
				effectiveEndDate: configResult.effectiveEndDate,
			};

			// Check for allocation ID - API may return it as 'id' or 'allocationId'
			const existingAllocationId = existingAllocation?.id || existingAllocation?.allocationId;

			if (existingAllocationId) {
				await this.allocationService
					.updateAllocation(existingAllocationId, allocationPayload)
					.toPromise();
				this.utilService.showSnackbar(
					'Volume allocation updated successfully.',
					SNACKBAR.SUCCESS,
					SNACKBAR.ACTION
				);
			} else {
				await this.allocationService.createAllocation(allocationPayload).toPromise();

				// If requested, add the carrier to Waterfall after creating the allocation
				// Per the plan: carriers with allocations are auto-added to the lowest priority group
				if (addToWaterfall) {
					await this.addCarrierToLowestPriorityGroup(carrierId, projectId);
				} else if (moveToLowestPriorityGroup && projectCarrierMapId) {
					// Move existing carrier to lowest priority group
					await this.moveCarrierToLowestPriorityGroup(projectCarrierMapId, projectId);
				} else {
					this.utilService.showSnackbar(
						'Volume allocation configured successfully.',
						SNACKBAR.SUCCESS,
						SNACKBAR.ACTION
					);
				}
			}

			// Reload allocations in the store to update the UI
			this.store.dispatch(PROJECT_DETAILS.ALLOCATIONS.LOAD({ projectId }));
		} catch (error) {
			console.error('Failed to configure allocation:', error);
			this.utilService.showSnackbar(
				'Failed to configure volume allocation. Please try again.',
				SNACKBAR.ERROR,
				SNACKBAR.ACTION
			);
		}
	}

	/**
	 * Adds a carrier to the lowest priority group in the waterfall.
	 * Per the Volume Based Allocation plan:
	 * - If multiple groups exist → add to lowest priority group (highest delay)
	 * - If only one group exists → create new "Allocated Carriers" group and add carrier there
	 * - This ensures non-allocated carriers get first priority in the waterfall
	 */
	async addCarrierToLowestPriorityGroup(carrierId: string, projectId: string): Promise<void> {
		try {
			// Get or create the lowest priority group
			const { projectGroupId, projectHasNoGroups, groupCreated } =
				await this.projectGroupService.getLowestPriorityGroup(projectId);

			if (projectHasNoGroups) {
				this.utilService.showSnackbar(
					'Volume allocation configured. Please add carrier to a waterfall group manually.',
					SNACKBAR.SUCCESS,
					SNACKBAR.ACTION
				);
				return;
			}

			// Build payload to add carrier to the lowest priority group
			const payload = {
				carrierIDs: [carrierId],
				desiredPrice: null,
				carrierRespondedAt: null,
				status: PROJECT_CARRIER_MAP_STATUS.PROCURED,
				projectGroupId,
			};

			// Add carrier to waterfall
			const procuredCarriersResponse = await this.restService
				.POST(this.urlService.addProjectCarrierMap(projectId), payload)
				.toPromise();

			// Update the store with the new carrier
			const projectDetailsPageState = await firstValueFrom(
				this.store.select((state) => state[projectDetailsPageStateKey]).pipe(take(1))
			);

			await this.afterMovingCarriersFromRecommendedToProcure(
				[carrierId],
				procuredCarriersResponse,
				projectDetailsPageState
			);

			// If a new group was created, refresh project groups in the store
			if (groupCreated) {
				this.projectDetailsEffects.setProjectGroups(
					projectDetailsPageState.allProjectCarrierMapsList.concat(procuredCarriersResponse)
				);
			}
		} catch (error) {
			console.error('Failed to add carrier to waterfall:', error);
			this.utilService.showSnackbar(
				'Volume allocation configured but failed to add carrier to waterfall.',
				SNACKBAR.WARNING,
				SNACKBAR.ACTION
			);
		}
	}

	/**
	 * Moves an existing carrier (already in waterfall) to the lowest priority group.
	 * Per the Volume Based Allocation plan:
	 * - If multiple groups exist → move to lowest priority group (highest delay)
	 * - If only one group exists → create new "Allocated Carriers" group and move carrier there
	 * - This ensures non-allocated carriers get first priority in the waterfall
	 */
	async moveCarrierToLowestPriorityGroup(projectCarrierMapId: string, projectId: string): Promise<void> {
		try {
			// Get the current state to find the carrier's current group
			const state = await this.store
				.select(projectDetailsPageStateKey)
				.pipe(take(1))
				.toPromise();

			// Find the project carrier map
			const projectCarrierMap = state.allProjectCarrierMapsList.find(
				(pcm) => pcm.id === projectCarrierMapId
			);

			if (!projectCarrierMap) {
				console.error('Could not find project carrier map:', projectCarrierMapId);
				this.utilService.showSnackbar(
					'Volume allocation configured successfully.',
					SNACKBAR.SUCCESS,
					SNACKBAR.ACTION
				);
				return;
			}

			// Get or create the lowest priority group
			const { projectGroupId, projectHasNoGroups, groupCreated } =
				await this.projectGroupService.getLowestPriorityGroup(projectId);

			if (projectHasNoGroups) {
				this.utilService.showSnackbar(
					'Volume allocation configured. Please move carrier to lowest priority group manually.',
					SNACKBAR.SUCCESS,
					SNACKBAR.ACTION
				);
				return;
			}

			// Check if carrier is already in the lowest priority group
			if (projectCarrierMap.projectGroup?.id === projectGroupId) {
				this.utilService.showSnackbar(
					'Volume allocation configured. Carrier is already in lowest priority group.',
					SNACKBAR.SUCCESS,
					SNACKBAR.ACTION
				);

				// Still need to refresh groups if a new one was created
				if (groupCreated) {
					this.projectDetailsEffects.setProjectGroups(state.allProjectCarrierMapsList);
				}

				return;
			}

			// Build the update request to move the carrier
			const projectGroupUpdateRequest = { projectGroups: [] };
			const deleteGroupIds: string[] = [];

			// If carrier is in a group, remove them from that group
			if (projectCarrierMap.projectGroup?.id) {
				const sourceGroup = state.projectGroups.find(
					(group) => group.id === projectCarrierMap.projectGroup.id
				);

				if (sourceGroup) {
					const remainingCarrierMapIds = sourceGroup.projectCarrierMapsList
						.filter((pcm) => pcm.id !== projectCarrierMapId)
						.map((pcm) => pcm.id);

					// If no carriers left in the source group, mark it for deletion
					// (unless it's a system group)
					if (remainingCarrierMapIds.length === 0 && !sourceGroup.systemGroup) {
						deleteGroupIds.push(sourceGroup.id);
					} else if (remainingCarrierMapIds.length > 0) {
						projectGroupUpdateRequest.projectGroups.push({
							projectGroupId: sourceGroup.id,
							projectCarrierMapIdList: remainingCarrierMapIds,
						});
					}
				}
			}

			// Add carrier to the destination (lowest priority) group
			const destGroup = state.projectGroups.find((group) => group.id === projectGroupId);
			const destCarrierMapIds = destGroup
				? destGroup.projectCarrierMapsList.map((pcm) => pcm.id)
				: [];

			projectGroupUpdateRequest.projectGroups.push({
				projectGroupId: projectGroupId,
				projectCarrierMapIdList: [...destCarrierMapIds, projectCarrierMapId],
			});

			// Dispatch the action to move the carrier
			this.store.dispatch(
				PROJECT_GROUPS.ADD_TO_GROUP.ADD({
					updateGroups: projectGroupUpdateRequest,
					deleteGroupIds,
				})
			);

			this.utilService.showSnackbar(
				'Volume allocation configured. Carrier moved to lowest priority group.',
				SNACKBAR.SUCCESS,
				SNACKBAR.ACTION
			);
		} catch (error) {
			console.error('Failed to move carrier to lowest priority group:', error);
			this.utilService.showSnackbar(
				'Volume allocation configured. Could not move carrier to lowest priority group.',
				SNACKBAR.WARNING,
				SNACKBAR.ACTION
			);
		}
	}

	/**
	 * Check if the project is a workflow project (Waterfall or Dedicated Lane)
	 */
	isWorkflowProject(projectDetailsPageState: ProjectDetailsState): boolean {
		return IS_WORKFLOW_PROJECT_TYPE(projectDetailsPageState.projectDTO?.type);
	}
}
